import { useMemo } from "react";

import { useMessageTemplates } from "src-next/modules/crm-clinical/messageTemplates/hooks/useMessageTemplates";
import {
  isAnyFluxmedDefaultMessageTemplate,
  isMessageTemplateListedForSend,
} from "src-next/modules/crm-clinical/messageTemplates/utils/messageTemplateUiUtils";
import type { MessageTemplate } from "src-next/modules/crm-clinical/messageTemplates/types";

import { useWhatsAppEndpoint } from "./useWhatsAppEndpoint";
import type { MessagingTemplate } from "../types";

interface UseMessagingTemplatesForSendParams {
  /**
   * Contexto (tenant) usado para:
   * - resolver Endpoint ativo (rank 1) no FHIR (WABA / phone-number-id)
   * - filtrar templates por prefixo de organização (ex.: `123_template_x`)
   */
  organizationId?: string;
  /**
   * Fonte opcional de templates já carregados por outro fluxo (ex.: CRM management).
   *
   * Quando fornecido, este hook NÃO faz nova busca em `useMessageTemplates`,
   * evitando double-fetch e mantendo a transformação/filtro centralizados.
   */
  sourceTemplates?: MessageTemplate[];
  /**
   * `false` não dispara listagem. Se o board já tiver a chave do mesmo WABA,
   * a memória ainda é devolvida. O filtro de envio continua em cima da lista completa.
   */
  enabled?: boolean;
}

/** Lista completa do cache → contrato do dialog. Não cachear este array filtrado. */
export function mapRawTemplatesToMessagingTemplates(
  rawTemplates: MessageTemplate[],
  organizationId?: string,
): MessagingTemplate[] {
  return rawTemplates
    .filter(
      (template) =>
        !isAnyFluxmedDefaultMessageTemplate(template.name, organizationId),
    )
    .filter((template) => isMessageTemplateListedForSend(template.status))
    .map((template) => ({
      id: template.id,
      name: template.name,
      preview: template.preview,
      header: template.header || undefined,
      status: template.status,
      buttonValue: template.buttonValue || undefined,
    }));
}

interface UseMessagingTemplatesForSendResult {
  /**
   * Templates já normalizados para o contrato do módulo de messaging.
   * A lista deve ser consistente entre todos os callers (agenda, busca, seção de templates).
   */
  templates: MessagingTemplate[];
  /**
   * Loading consolidado (Endpoint + templates).
   */
  isLoading: boolean;
  /**
   * Exposto para UI / debug: se não houver WABA rank1 ainda, a busca de templates fica desabilitada
   * para evitar "double-fetch" (DEFAULT → WABA real) e evitar templates do WABA errado.
   */
  wabaIdRank1: string | null;
}

/**
 * useMessagingTemplatesForSend
 *
 * Ponto único para listar templates elegíveis para envio pelo `SendWhatsAppMessageDialog`.
 *
 * Regras:
 * - Resolve WABA rank1 via Endpoint ativo (FHIR) com `useWhatsAppEndpoint`.
 * - Só busca templates quando a resolução do Endpoint terminar (enabled).
 *   - Se existir `wabaIdRank1`, busca os templates do WABA preferencial (rank 1).
 *   - Se NÃO existir endpoint ativo (`wabaIdRank1` nulo), busca templates DEFAULT.
 * - Filtra por organização e status elegível para envio.
 *
 * Motivação:
 * - Evitar divergência entre telas (MessageTemplatesSection, PatientSearch, ScheduleQueue).
 * - Evitar busca baseada em `metaConnection.wabaId` (setup), que pode divergir do Endpoint ativo.
 * - Evitar "double-fetch" com DEFAULT enquanto a resolução de Endpoint ainda não terminou.
 */
export function useMessagingTemplatesForSend(
  params: UseMessagingTemplatesForSendParams = {},
): UseMessagingTemplatesForSendResult {
  const { wabaIdRank1, phoneNumberIdRank1, isLoading: isEndpointLoading } =
    useWhatsAppEndpoint({
      organizationId: params.organizationId,
    });

  const shouldFetchTemplates =
    (params.enabled ?? true) && !params.sourceTemplates && !isEndpointLoading;

  const { templates: fetchedTemplates, isLoading: isTemplatesLoading } =
    useMessageTemplates({
      whatsappBusinessAccountId: wabaIdRank1 ?? undefined,
      phoneNumberId: phoneNumberIdRank1,
      organizationId: params.organizationId,
      enabled: shouldFetchTemplates,
    });

  const rawTemplates = params.sourceTemplates ?? fetchedTemplates;

  const templates = useMemo<MessagingTemplate[]>(
    () => mapRawTemplatesToMessagingTemplates(rawTemplates, params.organizationId),
    [params.organizationId, rawTemplates],
  );

  return {
    templates,
    isLoading: params.sourceTemplates ? false : isEndpointLoading || isTemplatesLoading,
    wabaIdRank1,
  };
}

